import { useState, useEffect, useRef } from "react";
import { acquireBackendApiToken } from "../../../utils/msalTokenService";

interface MaintenanceModeStatus {
  enabled: boolean;
  message: string | null;
}

// Use objects API for settings (different from audit API)
const OBJECTS_API_BASE_URL = import.meta.env.VITE_DEALS_API_BASE_URL;
const MAINTENANCE_URL = `${OBJECTS_API_BASE_URL}/api/objects/settings/maintenance-mode`;

/** How long the "saved" confirmation stays on screen. */
const SUCCESS_MESSAGE_MS = 3000;

const FORBIDDEN = 403;

export interface MaintenanceMode {
  loading: boolean;
  saving: boolean;
  error: string | null;
  success: string | null;
  enabled: boolean;
  message: string;
  setEnabled: (enabled: boolean) => void;
  setMessage: (message: string) => void;
  save: () => Promise<void>;
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : "An unknown error occurred";
}

async function postMaintenanceMode(
  enabled: boolean,
  message: string
): Promise<MaintenanceModeStatus> {
  const token = await acquireBackendApiToken();

  const response = await fetch(MAINTENANCE_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ enabled, message: message || null }),
  });

  if (response.status === FORBIDDEN) {
    throw new Error("Access denied. You must be an admin to change settings.");
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to save settings: ${response.status} - ${errorText}`);
  }

  return (await response.json()) as MaintenanceModeStatus;
}

/** Maintenance-mode status, backed by the objects API. */
export function useMaintenanceMode(): MaintenanceMode {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  // Fetch current maintenance mode status
  useEffect(() => {
    const fetchMaintenanceMode = async (): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(MAINTENANCE_URL);
        if (!response.ok) {
          throw new Error(`Failed to fetch maintenance mode: ${response.status}`);
        }

        const status = (await response.json()) as MaintenanceModeStatus;
        setEnabled(status.enabled);
        setMessage(status.message || "");
      } catch (err) {
        setError(errorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    void fetchMaintenanceMode();
  }, []);

  const save = async (): Promise<void> => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const status = await postMaintenanceMode(enabled, message);
      setEnabled(status.enabled);
      setMessage(status.message || "");
      setSuccess("Settings saved successfully!");

      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      successTimeoutRef.current = setTimeout(() => setSuccess(null), SUCCESS_MESSAGE_MS);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return { loading, saving, error, success, enabled, message, setEnabled, setMessage, save };
}
